const STORAGE_KEY = 'app-customization'

export const DEFAULT_BUTTON_COLOR = '#8c5a3d'
export const DEFAULT_SECONDARY_COLOR = '#704832'

// #8c5a3d is the reserved site identity color (used by the user profile).
// A company cannot use it as a brand color so the site chrome stays distinguishable.
export const RESERVED_SITE_COLOR = DEFAULT_BUTTON_COLOR

export function isReservedSiteColor(color) {
  return typeof color === 'string' && color.trim().toLowerCase() === RESERVED_SITE_COLOR.toLowerCase()
}

export function isValidHexColor(color) {
  return typeof color === 'string' && /^#[0-9a-fA-F]{6}$/.test(color)
}

export function darkenColor(color, amount) {
  const hex = color.replace('#', '')
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  const avgBrightness = (r + g + b) / 3
  const adjustedAmount = avgBrightness < 80 ? amount * 0.5 : amount

  const newR = Math.max(0, Math.floor(r * (1 - adjustedAmount)))
  const newG = Math.max(0, Math.floor(g * (1 - adjustedAmount)))
  const newB = Math.max(0, Math.floor(b * (1 - adjustedAmount)))

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

export function lightenColor(color, amount) {
  const hex = color.replace('#', '')
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)

  const newR = Math.min(255, Math.floor(r + (255 - r) * amount))
  const newG = Math.min(255, Math.floor(g + (255 - g) * amount))
  const newB = Math.min(255, Math.floor(b + (255 - b) * amount))

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`
}

export function getContrastTextColor(color) {
  const normalized = isValidHexColor(color) ? color : DEFAULT_BUTTON_COLOR
  const hex = normalized.replace('#', '')
  const weights = [0.2126, 0.7152, 0.0722]
  const luminance = [0, 2, 4]
    .map((offset) => parseInt(hex.slice(offset, offset + 2), 16) / 255)
    .map((channel) => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4)
    .reduce((sum, channel, index) => sum + channel * weights[index], 0)

  const whiteContrast = 1.05 / (luminance + 0.05)
  const darkContrast = (luminance + 0.05) / 0.05
  return whiteContrast >= darkContrast ? '#ffffff' : '#111827'
}

export function normalizeAppearance(settings) {
  const buttonColor = isValidHexColor(settings?.buttonColor)
    ? settings.buttonColor.trim()
    : DEFAULT_BUTTON_COLOR
  const secondaryColor = isValidHexColor(settings?.secondaryColor)
    ? settings.secondaryColor.trim()
    : DEFAULT_SECONDARY_COLOR

  return { buttonColor, secondaryColor }
}

export function applyBrandingFromCompany(source) {
  if (!source || typeof source !== 'object') {
    applyCustomization(normalizeAppearance({}))
    return
  }
  const appearance =
    (source.appearance && typeof source.appearance === 'object')
      ? source.appearance
      : source
  applyCustomization(normalizeAppearance(appearance))
}

export function applyDefaultBranding() {
  applyCustomization(normalizeAppearance({}))
}


export function saveAssetsToStorage(assets) {
  const current = _loadRaw()
  localStorage.setItem('app-customization', JSON.stringify({
    ...current,
    logoCompany: null,
    bannerCompany: null,
  }))
}

export function saveCustomizationToStorage(settings) {
  const existing = _loadRaw()
  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    logoCompany: existing.logoCompany ?? null,
    bannerCompany: existing.bannerCompany ?? null,
    ...normalizeAppearance(settings)
  }))
}

function _loadRaw() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

export function loadAssetsFromStorage() {
  const parsed = _loadRaw()
  return {
    logoCompany: parsed?.logoCompany ?? null,
    bannerCompany: parsed?.bannerCompany ?? null,
  }
}

/**
 * Build the full map of branding CSS custom properties for a given appearance.
 * Returned as `{ '--var': value }` so it can be applied to `:root` globally or
 * bound inline on a single element to scope a theme to its subtree (e.g. the
 * user profile keeping the site default color regardless of company branding).
 */
export function computeBrandingVars(settings) {
  const normalized = normalizeAppearance(settings)
  const baseColor = normalized.buttonColor

  const hoverColor = darkenColor(baseColor, 0.15)
  const activeColor = darkenColor(baseColor, 0.25)
  const focusColor = lightenColor(baseColor, 0.1)
  const ctaTextColor = getContrastTextColor(baseColor)

  return {
    '--primary-accent-color': baseColor,
    '--secondary-accent-color': normalized.secondaryColor,
    '--primary-accent-hover': hoverColor,
    '--primary-accent-active': activeColor,
    '--primary-accent-focus': focusColor,
    '--light-accent': baseColor,
    '--light-accent-hover': hoverColor,
    '--light-accent-active': activeColor,
    '--light-cta-bg': baseColor,
    '--light-cta-bg-hover': hoverColor,
    '--light-cta-bg-active': activeColor,
    '--light-cta-text': ctaTextColor,
    '--dark-accent': baseColor,
    '--dark-accent-hover': hoverColor,
    '--dark-accent-active': activeColor,
    '--dark-cta-bg': baseColor,
    '--dark-cta-bg-hover': hoverColor,
    '--dark-cta-bg-active': activeColor,
    '--dark-cta-text': ctaTextColor
  }
}

export function applyCustomization(settings) {
  const vars = computeBrandingVars(settings)
  const root = document.documentElement
  for (const [name, value] of Object.entries(vars)) {
    root.style.setProperty(name, value)
  }
  return true
}

export function applyBrandingFromLocalStorage() {
  try {
    const raw = localStorage.getItem('business_data')
    if (raw) {
      const business = JSON.parse(raw)
      if (business?.appearance && typeof business.appearance === 'object') {
        const normalized = normalizeAppearance(business.appearance)
        if (isValidHexColor(normalized.buttonColor)) {
          applyCustomization(normalized)
          return
        }
      }
    }
  } catch {
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        const normalized = normalizeAppearance(parsed)
        if (isValidHexColor(normalized.buttonColor)) {
          applyCustomization(normalized)
          return
        }
      }
    }
  } catch {
  }
  applyCustomization(normalizeAppearance({}))
}

export function loadCustomizationFromStorage() {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (!raw) return normalizeAppearance()
  try {
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object'
      ? normalizeAppearance(parsed)
      : normalizeAppearance()
  } catch {
    return normalizeAppearance()
  }
}

export function resolveCompanyLogoUrl(company) {
  if (!company || typeof company !== 'object') return null
  return (
    company.logoCompany ??
    company.logo_company ??
    company.logo_url ??
    company.logo ??
    null
  )
}

export function resolveCompanyBannerUrl(company) {
  if (!company || typeof company !== 'object') return null
  return company.bannerCompany ?? company.banner_company ?? company.banner ?? null
}

export function applyCompanyBrandingFromApi(company) {
  if (!company || typeof company !== 'object') return false

  const logoUrl = resolveCompanyLogoUrl(company)
  const bannerUrl = resolveCompanyBannerUrl(company)
  if (logoUrl || bannerUrl) {
    saveAssetsToStorage({
      logoCompany: logoUrl ?? null,
      bannerCompany: bannerUrl ?? null
    })
  }

  const a = company.appearance
  if (a && typeof a === 'object' && (isValidHexColor(a.buttonColor) || isValidHexColor(a.secondaryColor))) {
    const appearance = normalizeAppearance(a)
    saveCustomizationToStorage(appearance)
    applyCustomization(appearance)
  }

  return true
}

export function getBrandingBusinessPatch(company) {
  if (!company || typeof company !== 'object') return {}
  const patch = {}
  if (company.id != null) patch.id = company.id
  if (company.tenant_id) patch.tenant_id = company.tenant_id
  if (company.name) patch.name = company.name
  if (company.email) patch.email = company.email
  if (company.phone) patch.phone = company.phone
  if (company.address) patch.address = company.address
  if (company.description) patch.description = company.description

  // typebusiness es la fuente de verdad; description solo cubre filas viejas que no lo tienen.
  const businessType = company.business_type_id ?? company.businessTypeId ?? company.business_type?.id ?? company.business_type ?? company.businessType ?? company.typebusiness ?? company.type ?? company.description
  if (businessType) patch.type = businessType

  const logoUrl = resolveCompanyLogoUrl(company)
  const bannerUrl = resolveCompanyBannerUrl(company)
  if (logoUrl) {
    patch.logoCompany = logoUrl
    patch.logo = logoUrl
  }
  if (bannerUrl) patch.bannerCompany = bannerUrl

  const a = company.appearance
  if (a && typeof a === 'object') {
    patch.appearance = normalizeAppearance(a)
  }

  return patch
}
